import { Heading } from '@ksyos/design-system';
import { useEffect, useMemo, useState } from 'react';
import type { EpisodeCategory } from '@common/enums';
import type { IServicesByServiceGroupForEpisodeCategory } from '@common/interfaces';
import type { IDropdownMenuItem } from '@frontend/components/elements/ButtonHeader';
import { ManagerAuthorization } from '@common/enums';
import { isActiveOnDateOrInTheFuture } from '@common/utils/isActiveOnDate';
import { AddButtonHeader } from '@frontend/components/elements/AddButtonHeader';
import { ButtonHeader } from '@frontend/components/elements/ButtonHeader';
import { translateKey } from '@frontend/translate';
import { hasOneAuthorization, useAbortableFetch } from '@frontend/utils/';
import type { IServiceGroupTab } from '../../PersonalSettingsCarePath';
import type { IPersonalSettingsData } from '../../types';
import { ServiceRightStopModal } from '../ServiceRightStopModal';

type HeaderButtonsProps = {
    employmentId: string;
    episodeCategory: EpisodeCategory;
    personalSettingsData: IPersonalSettingsData;
    reloadServiceRight: () => Promise<void>;
    selectedServiceGroupTab?: IServiceGroupTab;
    serviceGroupNames: string;
};
export function HeaderButtons(props: HeaderButtonsProps) {
    const today = new Date();
    const {
        employmentId,
        episodeCategory,
        personalSettingsData,
        reloadServiceRight,
        selectedServiceGroupTab,
        serviceGroupNames,
    } = props;
    const abortableFetch = useAbortableFetch();
    const inactiveSettingsName = translateKey('service.inactiveServices');

    const [serviceGroupMembership, setServiceGroupMembership] = useState<
        IServicesByServiceGroupForEpisodeCategory | undefined
    >();
    const [showServiceRightStopModal, setShowServiceRightStopModal] = useState(false);

    const canEdit = hasOneAuthorization(ManagerAuthorization.employmentSettingsEdit);

    const dropdownMenuItems: IDropdownMenuItem[] | undefined = useMemo(() => {
        const episodeCategoryServiceRights = [
            ...personalSettingsData.serviceConsumers,
            ...personalSettingsData.serviceProviders,
            ...(personalSettingsData.serviceSupervisors || []),
            ...(personalSettingsData.serviceReaders || []),
            ...personalSettingsData.serviceEmployments,
        ].filter((serviceRight) => serviceRight.service.episodeCategory.name === episodeCategory);

        const ServiceRightsWithoutEndDate = episodeCategoryServiceRights
            .filter((serviceRight) => isActiveOnDateOrInTheFuture(serviceRight, today))
            .some((serviceRight) => !serviceRight.endDate);

        if (ServiceRightsWithoutEndDate && selectedServiceGroupTab?.displayText !== inactiveSettingsName && canEdit) {
            return [
                {
                    text: translateKey(
                        'carePath.stopEpisodeCategory',
                        translateKey(`episodeCategoryEnum.${episodeCategory}`)
                    ),
                    onClick: () => setShowServiceRightStopModal(true),
                },
            ];
        }

        return undefined;
    }, [personalSettingsData]);

    useEffect(() => {
        if (canEdit) {
            loadAvailableCarePathsWithServiceGroups();
        }
    }, [employmentId, episodeCategory]);

    return (
        <>
            {canEdit && serviceGroupMembership && serviceGroupMembership.serviceGroups.length > 0 ? (
                <AddButtonHeader
                    buttonText={translateKey('service.addServiceButton')}
                    dropdownMenuItems={canEdit ? dropdownMenuItems : undefined}
                    hideButton={!canEdit}
                    link={`/employments/${employmentId}/personalSettings/addCarePath/${episodeCategory}`}
                    style={{ marginTop: 0 }}
                    title={<Heading size="xlarge">{translateKey(`episodeCategoryEnum.${episodeCategory}`)}</Heading>}
                />
            ) : (
                <ButtonHeader
                    dropdownMenuItems={canEdit ? dropdownMenuItems : undefined}
                    title={<Heading size="xlarge">{translateKey(`episodeCategoryEnum.${episodeCategory}`)}</Heading>}
                />
            )}

            {showServiceRightStopModal && (
                <ServiceRightStopModal
                    alertMessage={translateKey(
                        'serviceRightStopModal.warningServiceGroup',
                        translateKey(`episodeCategoryEnum.${props.episodeCategory}`)
                    )}
                    bodyText={translateKey(
                        'serviceRightStopModal.bodyEpisodeCategory',
                        translateKey(`episodeCategoryEnum.${props.episodeCategory}`),
                        serviceGroupNames
                    )}
                    endDate={today}
                    onClose={() => setShowServiceRightStopModal(false)}
                    organizationId={personalSettingsData.organization.id}
                    reloadServiceRights={reloadServiceRight}
                    serviceConsumerRights={personalSettingsData.serviceConsumers.filter(
                        (serviceRight) => serviceRight.service.episodeCategory.name === props.episodeCategory
                    )}
                    serviceEmploymentRights={
                        personalSettingsData.serviceEmployments?.filter(
                            (serviceRight) => serviceRight.service.episodeCategory.name === props.episodeCategory
                        ) || []
                    }
                    serviceProviderRights={personalSettingsData.serviceProviders.filter(
                        (serviceRight) => serviceRight.service.episodeCategory.name === props.episodeCategory
                    )}
                    serviceReaderRights={
                        personalSettingsData.serviceReaders?.filter(
                            (serviceRight) => serviceRight.service.episodeCategory.name === props.episodeCategory
                        ) || []
                    }
                    serviceSupervisorRights={
                        personalSettingsData.serviceSupervisors?.filter(
                            (serviceRight) => serviceRight.service.episodeCategory.name === props.episodeCategory
                        ) || []
                    }
                    stopServiceGroup={false}
                    title={translateKey(
                        'carePath.stopServiceGroup',
                        translateKey(`episodeCategoryEnum.${props.episodeCategory}`)
                    )}
                />
            )}
        </>
    );

    async function loadAvailableCarePathsWithServiceGroups() {
        const url = `/api/employments/${props.employmentId}/addableServiceGroups?episodeCategory=${props.episodeCategory}`;
        const result = await abortableFetch.fetchJson(url, 'GET');

        const serviceGroupMembershipsGroupedByEpisodeCategories = result.json
            .items as IServicesByServiceGroupForEpisodeCategory[];

        let serviceGroupMembershipsGroupedByEpisodeCategory = undefined;
        if (serviceGroupMembershipsGroupedByEpisodeCategories.length > 0) {
            serviceGroupMembershipsGroupedByEpisodeCategory = serviceGroupMembershipsGroupedByEpisodeCategories[0];
        }
        setServiceGroupMembership(serviceGroupMembershipsGroupedByEpisodeCategory);
    }
}
